import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Pencil, UserPlus } from "lucide-react";

import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { financialService, type GovernanceAccessScope, type GovernanceAccount, type Grant } from "../../services/financialService";
import { apiErrorMessage, apiFieldErrors } from "../../utils/apiError";
import {
  GRANT_SCOPES, SCOPE_FLAGS, accessConfig, accessPeriod, accessRows, accessStatus, canSee, changedFields, describeChanges,
  draftOf, draftParams, draftProblems, grantScopeLabel, roleLabel, shortDate, stamp, type AccessRow, type ScopeDraft,
} from "./governanceLogic";

const ACTIONS: Record<string, string> = { created: "Set up", changed: "Changed", ended: "Access ended" };
const danger = "mr-auto rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50";

/**
 * Board and donor logins and what each may see. Every change is recorded
 * with who made it and why; access is ended rather than deleted, so its
 * settings stay for a renewal.
 */
export function AccessPanel({ accounts, scopes, grants, isLoading, isError, onRetry, onChanged }: {
  accounts?: GovernanceAccount[]; scopes?: GovernanceAccessScope[]; grants: Grant[]
  isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [mode, setMode] = useState<"edit" | "end" | "setup" | null>(null);
  const done = (text: string) => { setNotice({ tone: "success", text }); onChanged(); };
  const close = () => { setViewing(null); setMode(null); };
  const rows = accessRows(accounts ?? [], scopes ?? []);
  const row = viewing ? rows.find((item) => item.account.id === viewing) : undefined;

  const setUp = useMutation({
    mutationFn: financialService.createGovernanceScope,
    onSuccess: (saved) => { setMode(null); done(`Access set up for ${saved.user_name || saved.username} with the ${roleLabel(saved.user_type).toLowerCase()} defaults. Change it here if they should see more or less.`); },
  });
  const save = useMutation({
    mutationFn: financialService.updateGovernanceScope,
    onSuccess: (saved) => { setMode(null); done(`Access changed for ${saved.user_name || saved.username}.`); },
  });
  const end = useMutation({
    mutationFn: financialService.endGovernanceScope,
    onSuccess: (saved) => { setMode(null); done(`${saved.user_name || saved.username} can no longer open the governance figures. Their settings are kept if you renew it.`); },
  });

  const columns: RegisterColumn<AccessRow>[] = [
    { header: "Name", cell: (item) => <span><span className="block font-medium text-gray-900">{item.account.name}</span><span className="text-xs text-gray-500">{item.account.username}</span></span>, exportValue: (item) => item.account.name },
    { header: "Role", cell: (item) => roleLabel(item.account.user_type), exportValue: (item) => roleLabel(item.account.user_type) },
    { header: "Can see", cell: (item) => (item.scope ? <span className="line-clamp-2 block min-w-[14rem] whitespace-normal text-sm">{canSee(item.scope).join(", ") || "Nothing yet"}</span> : <span className="text-gray-500">Role defaults when they first sign in</span>), exportValue: (item) => (item.scope ? canSee(item.scope).join("; ") : "Role defaults") },
    { header: "Salary", cell: (item) => (item.scope?.can_view_salary ? <span className="font-medium text-amber-800">Authorized</span> : "—"), exportValue: (item) => (item.scope?.can_view_salary ? "Authorized" : "") },
    { header: "Access period", cell: (item) => <span className="whitespace-nowrap">{accessPeriod(item.scope) || "—"}</span>, exportValue: (item) => accessPeriod(item.scope) },
    { header: "Status", cell: (item) => <StatusDot {...accessStatus(item.account, item.scope)} />, exportValue: (item) => accessStatus(item.account, item.scope).label },
    { header: "Last change", cell: () => "", exportValue: (item) => (item.scope?.history[0] ? `${stamp(item.scope.history[0].created_at)} ${item.scope.history[0].changed_by_name}: ${item.scope.history[0].reason}` : ""), exportOnly: true },
  ];

  const scope = row?.scope;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Board and donor access"
        description="Who outside the school's management can read the governance figures, and exactly what each may see. Access is read-only: nobody here can change a record. Board and donor logins are made under Users."
        rows={rows}
        config={accessConfig}
        columns={columns}
        rowKey={(item) => item.account.id}
        onOpen={(item) => { setMode(item.scope ? null : "setup"); setViewing(item.account.id); }}
        openLabel={(item) => (item.scope ? "Open" : "Set up")}
        noun={["login", "logins"]}
        exportName={{ file: "governance-access", title: "Board and Donor Access" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="There are no board or donor logins yet. Make one under Users with the Board Viewer or Donor Viewer role."
      />

      {row && scope && mode !== "edit" && (
        <DetailsModal
          size="3xl"
          title={row.account.name}
          headline={{ label: `${roleLabel(row.account.user_type)} · ${row.account.username}`, value: scope.status === "expired" ? "Access ended" : scope.status === "not_started" ? `Starts ${shortDate(scope.valid_from)}` : "Can read the figures", note: accessPeriod(scope) }}
          status={accessStatus(row.account, scope)}
          notice={!row.account.is_active ? <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">This login is turned off, so they cannot sign in whatever their access says.</p> : scope.can_view_salary ? <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">Salary authorization is on: they see monthly pay totals{scope.can_drill_down ? " and each person's pay" : ""}.</p> : undefined}
          facts={[
            ...SCOPE_FLAGS.map(([field, label]) => [label, scope[field] ? "Yes" : "No"] as [string, string]),
            ["Grants", scope.grant_scope === "selected" ? scope.grant_names.join(", ") || "None ticked" : grantScopeLabel(scope.grant_scope)],
            ["Last changed by", scope.configured_by_name || "—"],
          ]}
          onClose={close}
          footer={
            <>
              {scope.status !== "expired" && <button type="button" className={danger} onClick={() => { end.reset(); setMode("end"); }}>End access now</button>}
              <button type="button" className="btn btn-outline" onClick={close}>Close</button>
              <button type="button" className="btn btn-primary gap-1.5" onClick={() => { save.reset(); setMode("edit"); }}><Pencil className="h-4 w-4" /> {scope.status === "expired" ? "Renew or change" : "Change access"}</button>
            </>
          }
        >
          {mode === "end" && (
            <ReasonPanel
              title="End access now"
              description="They lose access from today. What they could see is kept, so it can be renewed later."
              label="Why is it ending"
              placeholder="e.g. Their term on the board ended at the AGM on 20 September."
              context={{ key: "financial.governance_note", params: { kind: "end_access", id: scope.id } }}
              button="End access"
              danger
              busy={end.isPending}
              error={end.error}
              errorText="Access could not be ended."
              onSubmit={(reason) => end.mutate({ id: scope.id, reason })}
            />
          )}
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">History</h4>
            {scope.history.length ? (
              <ol className="space-y-3 border-l border-gray-200 pl-4">
                {scope.history.map((change) => (
                  <li key={change.id} className="relative text-sm">
                    <span className="absolute -left-[1.3rem] top-1.5 h-2 w-2 rounded-full bg-primary-500" />
                    <p className="font-medium text-gray-900">{ACTIONS[change.action] ?? change.action} <span className="font-normal text-gray-500">· {stamp(change.created_at)} by {change.changed_by_name}</span></p>
                    {change.changes.length > 0 && <ul className="mt-0.5 list-disc pl-5 text-gray-700">{change.changes.map((line) => <li key={line}>{line}</li>)}</ul>}
                    {change.reason && <p className="mt-1 whitespace-pre-wrap break-words rounded-md bg-gray-50 px-3 py-2 text-gray-800">{change.reason}</p>}
                  </li>
                ))}
              </ol>
            ) : <p className="text-sm text-gray-500">No changes have been recorded since this access was set up.</p>}
          </section>
        </DetailsModal>
      )}

      {row && scope && mode === "edit" && (
        <ScopeForm
          scope={scope}
          grants={grants}
          busy={save.isPending}
          error={save.error}
          onSave={(changes) => save.mutate({ id: scope.id, ...changes })}
          onClose={() => setMode(null)}
        />
      )}

      {row && !scope && mode === "setup" && (
        <SetUpForm account={row.account} busy={setUp.isPending} error={setUp.isError ? apiErrorMessage(setUp.error, "Access could not be set up.") : undefined} onSave={(reason) => setUp.mutate({ user: row.account.id, reason })} onClose={close} />
      )}
    </div>
  );
}

function SetUpForm({ account, busy, error, onSave, onClose }: { account: GovernanceAccount; busy: boolean; error?: string; onSave: (reason: string) => void; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const board = account.user_type === "board_viewer";
  return (
    <FormModal id="governance-setup" title={`Set up access: ${account.name}`} busy={busy} error={error} saveLabel="Set up access" onClose={onClose} onSubmit={(event) => { event.preventDefault(); onSave(reason.trim()); }}>
      <div className="flex items-start gap-3 rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-900">
        <UserPlus className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          {board
            ? "A board member starts with the financial position, budgets, approvals, exceptions and all grants: totals only, no account detail and no pay."
            : "A donor starts with nothing but the grants you choose for them. Tick their grants after setting up."}{" "}
          You can change any of it afterwards.
        </p>
      </div>
      <Field label="Note for the record" optional hint="e.g. Joined the board at the AGM.">
        <textarea className="input mt-1" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </FormModal>
  );
}

function ScopeForm({ scope, grants, busy, error, onSave, onClose }: {
  scope: GovernanceAccessScope; grants: Grant[]; busy: boolean; error: unknown
  onSave: (changes: ReturnType<typeof changedFields> & { reason: string }) => void; onClose: () => void
}) {
  const [draft, setDraft] = useState<ScopeDraft>(() => draftOf(scope));
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const set = (changes: Partial<ScopeDraft>) => setDraft((current) => ({ ...current, ...changes }));
  const nameOf = (id: number) => grants.find((grant) => grant.id === id)?.name ?? scope.grant_names[scope.grants.indexOf(id)] ?? `Grant ${id}`;
  const changes = describeChanges(scope, draft, nameOf);
  const problems = draftProblems(draft);
  const server = apiFieldErrors(error, ["grants", "valid_from", "valid_until", "reason"], "The access could not be saved.");
  const errors = { ...server.fields, ...(tried ? problems : {}), ...(tried && reason.trim().length < 5 ? { reason: "Say why this access is changing." } : {}) };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (!changes.length || Object.keys(problems).length || reason.trim().length < 5) return;
    onSave({ ...changedFields(scope, draft), reason: reason.trim() });
  };
  const shown = grants.filter((grant) => grant.workflow_status === "approved" || draft.grants.includes(grant.id));
  return (
    <FormModal
      id="governance-scope"
      size="3xl"
      title={`Change access: ${scope.user_name || scope.username}`}
      busy={busy}
      error={server.message || undefined}
      saveLabel="Save the change"
      note="Read-only whatever you tick: they can never change a record."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="What they can see" description="Each part of the governance page, shown only when ticked." divided={false} columns={2}>
        {SCOPE_FLAGS.map(([field, label, hint]) => (
          <label key={field} className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${draft[field] ? "border-primary-300 bg-primary-50/50" : "border-gray-200"}`}>
            <input type="checkbox" className="mt-0.5" checked={draft[field]} onChange={(event) => set({ [field]: event.target.checked } as Partial<ScopeDraft>)} />
            <span>
              <span className="block font-medium text-gray-900">{label}</span>
              <span className="block text-xs text-gray-500">{hint}</span>
              {field === "can_view_salary" && draft.can_view_salary && <span className="mt-1 block text-xs font-medium text-amber-800">Confidential: only with the board's explicit decision.</span>}
            </span>
          </label>
        ))}
      </FormSection>

      <FormSection title="Grants" description="Donors usually see only the grants they fund." columns={1}>
        <div role="radiogroup" aria-label="Which grants" className="segmented w-fit">
          {GRANT_SCOPES.map(([value, label, hint]) => (
            <button key={value} type="button" role="radio" aria-checked={draft.grant_scope === value} aria-pressed={draft.grant_scope === value} title={hint} className="segmented-option" onClick={() => set({ grant_scope: value })}>{label}</button>
          ))}
        </div>
        {draft.grant_scope === "selected" && (
          <Field label="Grants they may see" as="div" name="grants" errors={errors}>
            {shown.length ? (
              <div className="mt-1 grid max-h-56 gap-1 overflow-y-auto rounded-lg border border-gray-200 p-2 sm:grid-cols-2">
                {shown.map((grant) => (
                  <label key={grant.id} className="flex items-start gap-2 rounded px-2 py-1.5 text-sm font-normal hover:bg-gray-50">
                    <input type="checkbox" className="mt-0.5" checked={draft.grants.includes(grant.id)} onChange={(event) => set({ grants: event.target.checked ? [...draft.grants, grant.id] : draft.grants.filter((id) => id !== grant.id) })} />
                    <span><span className="block text-gray-900">{grant.name}</span><span className="text-xs text-gray-500">{grant.donor}{grant.workflow_status !== "approved" ? " · not yet approved, so not shown" : ""}</span></span>
                  </label>
                ))}
              </div>
            ) : <p className="mt-1 text-sm font-normal text-gray-500">No grants have been approved yet.</p>}
          </Field>
        )}
      </FormSection>

      <FormSection title="Access period" description="Leave both empty for access with no dates. They cannot open the page outside these dates.">
        <Field label="From" optional name="valid_from" errors={errors}>
          <input type="date" className="input mt-1" value={draft.valid_from} max={draft.valid_until || undefined} onChange={(event) => set({ valid_from: event.target.value })} />
        </Field>
        <Field label="Until" optional name="valid_until" errors={errors} hint="The last day they can read the figures.">
          <input type="date" className="input mt-1" value={draft.valid_until} min={draft.valid_from || undefined} onChange={(event) => set({ valid_until: event.target.value })} />
        </Field>
      </FormSection>

      <FormSection title="Why" description="Kept in this access's history with your name." columns={1}>
        <div className={`rounded-lg border px-4 py-3 text-sm ${changes.length ? "border-primary-200 bg-primary-50/60" : "border-dashed border-gray-300 text-gray-500"}`}>
          <p className="flex items-center gap-1.5 font-medium text-gray-900"><KeyRound className="h-4 w-4" /> What will change</p>
          {changes.length ? <ul className="mt-1 list-disc pl-5 text-gray-700">{changes.map((line) => <li key={line}>{line}</li>)}</ul> : <p className={`mt-1 ${tried ? "font-medium text-red-700" : ""}`}>Nothing yet. Tick or untick above{tried ? " before saving" : ""}.</p>}
        </div>
        <Field label="Reason for the change" as="div" name="reason" errors={errors}>
          <AIAssistField
            rows={2}
            value={reason}
            onChange={(next) => setReason(next)}
            placeholder="e.g. The board resolved on 12 September that the finance committee chair sees pay totals."
            context={{ key: "financial.governance_note", params: draftParams(scope, draft) }}
            draftLabel="Draft it"
            draftHint="Make the change above first. It words your notes, or leaves [the reason] for you: it never guesses why."
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
